import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, BookOpen, FileText, Award,
  Heart, User, Menu, X, GraduationCap, LogOut, ClipboardList,
  ShieldCheck, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { clearUser, getUser } from '@/lib/data';

const studentNav = [
  { label: 'Dashboard', icon: LayoutDashboard, to: '/dashboard' },
  { label: 'My Courses', icon: BookOpen, to: '/my-courses' },
  { label: 'Assignments', icon: FileText, to: '/assignments' },
  { label: 'Quizzes', icon: ClipboardList, to: '/quizzes' },
  { label: 'Certificates', icon: Award, to: '/certificates' },
  // { label: 'Wishlist', icon: Heart, to: '/wishlist' },
  // { label: 'Profile', icon: User, to: '/profile' },
];

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const user = getUser();
  const role = user?.role || 'student';
  const roleLabel = role === 'admin' ? 'Admin' : role === 'instructor' ? 'Instructor' : 'Student';

  const NavLinks = ({ onClick }) => (
    <div className="space-y-0.5">
      {/* <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-3 mb-2 mt-1">Student</p> */}
      {studentNav.map(item => {
        const active = location.pathname === item.to;
        return (
          <Link key={item.to} to={item.to} onClick={onClick}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
              active
                ? 'bg-primary text-primary-foreground shadow-md shadow-primary/25'
                : 'text-muted-foreground hover:bg-primary/8 hover:text-foreground'
            }`}>
            <item.icon className="w-4 h-4 flex-shrink-0" />
            {item.label}
            {active && (
              <motion.div layoutId="activeIndicator" className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-foreground/70" />
            )}
          </Link>
        );
      })}
      {/* <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-3 mb-2 mt-5">Other Dashboards</p>
      <Link to="/instructor" onClick={onClick}
        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-primary/8 hover:text-foreground transition-all">
        <BarChart3 className="w-4 h-4" /> Instructor Dashboard
      </Link>
      <Link to="/admin" onClick={onClick}
        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-primary/8 hover:text-foreground transition-all">
        <ShieldCheck className="w-4 h-4" /> Admin Panel
      </Link> */}
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar — glass style */}
      <aside className="hidden lg:flex w-64 flex-col fixed top-0 left-0 bottom-0 z-40 glass border-r border-black/5">
        <div className="p-5 border-b border-black/5">
          <Link to="/" className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 bg-primary rounded-xl flex items-center justify-center shadow-lg shadow-primary/30">
              <GraduationCap className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">
              Xceed<span className="text-primary">Edge</span>
            </span>
          </Link>
          {/* <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl glass-yellow text-xs font-semibold">
            <User className="w-3 h-3 text-primary" />
            <span className="text-foreground">{roleLabel}</span>
          </div> */}
        </div>
        <nav className="flex-1 p-3 overflow-y-auto">
          <NavLinks />
        </nav>
        <div className="p-3 border-t border-black/5">
          <button
            onClick={() => { clearUser(); window.location.href = '/'; }}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-primary/8 hover:text-foreground transition-all w-full"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 glass border-b border-black/5">
        <div className="flex items-center justify-between p-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-xl flex items-center justify-center shadow-lg shadow-primary/25">
              <GraduationCap className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">XceedEdge</span>
          </Link>
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} className="rounded-xl">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="lg:hidden fixed top-0 left-0 bottom-0 w-64 glass z-50 border-r border-black/5 overflow-y-auto"
            >
              <div className="p-5 border-b border-black/5">
                <span className="font-heading font-bold text-lg tracking-tight">XceedEdge</span>
              </div>
              <nav className="p-3">
                <NavLinks onClick={() => setSidebarOpen(false)} />
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl">
          <Outlet />
        </div>
      </div>
    </div>
  );
}